import React, { useEffect, useMemo, useRef, useState } from "react";

import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Stack,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import RecordVoiceOverOutlinedIcon from "@mui/icons-material/RecordVoiceOverOutlined";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";

// =====================================================
// 2026 PAGE COMPONENTS
// Adjust these paths if your folder structure is different
// =====================================================

import AboutSummerResidency2026 from "../SummerWorkshop2026/AboutSummerResidency2026";
import Participants2026 from "../SummerWorkshop2026/Participants2026";
import GuestSpeakers from "../../components/Speakers/Speakers2026";
import ScheduleSummer2026 from "../ScheduleSummer2026/ScheduleSummer2026";
import WeeklyReadings from "../WorkshopMaterialsSummer2026/WorkshopMaterialsSummer2026";

const SummerWorkshopArchive2026 = () => {
  // =====================================================
  // PAGE TITLE
  // =====================================================

  useEffect(() => {
    document.title = "Summer Residency 2026 Archive - MS Digital Hub";
  }, []);

  // =====================================================
  // SECTION REFS
  // Used by Quick Links
  // =====================================================

  const aboutRef = useRef(null);
  const participantsRef = useRef(null);
  const speakersRef = useRef(null);
  const scheduleRef = useRef(null);
  const materialsRef = useRef(null);

  const scrollTo = (ref) => {
    ref?.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  // =====================================================
  // ACCORDION STATE
  // =====================================================

  const [open, setOpen] = useState({
    about: true,
    participants: false,
    speakers: false,
    schedule: true,
    materials: false,
  });

  const expandAll = () => {
    setOpen({
      about: true,
      participants: true,
      speakers: true,
      schedule: true,
      materials: true,
    });
  };

  const collapseAll = () => {
    setOpen({
      about: false,
      participants: false,
      speakers: false,
      schedule: false,
      materials: false,
    });
  };

  // =====================================================
  // ARCHIVE INFORMATION
  // =====================================================

  const archiveMeta = useMemo(
    () => [
      {
        label: "June 15 – July 24, 2026",
      },
      {
        label: "10 Participants",
      },
      {
        label: "Six-Week Residency",
      },
    ],
    []
  );

  // =====================================================
  // QUICK LINKS
  // =====================================================

  const quickLinks = useMemo(
    () => [
      {
        id: "about",
        title: "About",
        desc: "Overview of the 2026 Summer Residency and its goals.",
        icon: <InfoOutlinedIcon fontSize="large" />,
        chip: "Overview",
        onClick: () => scrollTo(aboutRef),
      },

      {
        id: "participants",
        title: "Participants & Projects",
        desc: "Meet the 2026 participants and explore their digital humanities projects.",
        icon: <GroupsOutlinedIcon fontSize="large" />,
        chip: "10 Scholars",
        onClick: () => scrollTo(participantsRef),
      },

      {
        id: "speakers",
        title: "Guest Speakers",
        desc: "Meet the visiting faculty, librarians, archivists, and cultural heritage professionals.",
        icon: <RecordVoiceOverOutlinedIcon fontSize="large" />,
        chip: "Speakers",
        onClick: () => scrollTo(speakersRef),
      },

      {
        id: "schedule",
        title: "Schedule",
        desc: "Explore the six-week residency schedule, workshops, and activities.",
        icon: <EventAvailableOutlinedIcon fontSize="large" />,
        chip: "6 Weeks",
        onClick: () => scrollTo(scheduleRef),
      },

      {
        id: "materials",
        title: "Materials",
        desc: "Weekly readings, research resources, and workshop links.",
        icon: <MenuBookOutlinedIcon fontSize="large" />,
        chip: "Resources",
        onClick: () => scrollTo(materialsRef),
      },
    ],
    []
  );

  // =====================================================
  // QUICK LINK CARD
  // Same visual structure as 2025 archive
  // =====================================================

  const QuickCard = ({ item }) => (
    <Card
      elevation={0}
      sx={{
        height: "100%",
        border: "1px solid #e8e8e8",
        borderRadius: 3,

        background:
          "linear-gradient(180deg, rgba(255,215,0,0.10) 0%, rgba(255,255,255,1) 45%)",

        transition:
          "transform 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease",

        "&:hover": {
          transform: "translateY(-3px)",
          borderColor: "#FFD700",
          boxShadow: "0 8px 20px rgba(0,0,0,0.05)",
        },
      }}
    >
      <CardContent
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
          height: "100%",

          "&:last-child": {
            pb: 2,
          },
        }}
      >
        {/* Icon + Chip */}

        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box
            sx={{
              color: "#111",
              mt: 0.5,
            }}
          >
            {item.icon}
          </Box>

          <Chip
            label={item.chip}
            size="small"
            variant="outlined"
            sx={{
              fontSize: 11,
              height: 22,
              borderRadius: "6px",
              color: "#555",
              backgroundColor: "#f5f5f5",
            }}
          />
        </Box>

        {/* Title */}

        <Typography
          variant="h6"
          sx={{
            lineHeight: 1.2,
          }}
        >
          {item.title}
        </Typography>

        {/* Description */}

        <Typography
          variant="body2"
          sx={{
            color: "#444",
            lineHeight: 1.7,
            minHeight: "3.6rem",
          }}
        >
          {item.desc}
        </Typography>

        {/* Button */}

        <Button
          variant="contained"
          onClick={item.onClick}
          sx={{
            mt: "auto",

            backgroundColor: "#FFD700",
            color: "#111",

            borderRadius: 2,

            "&:hover": {
              backgroundColor: "#FFB800",
            },
          }}
        >
          Jump to section
        </Button>
      </CardContent>
    </Card>
  );

  // =====================================================
  // ACCORDION WRAPPER
  // Same structure used by the 2025 archive
  // =====================================================

  const SectionShell = ({
    title,
    subtitle,
    refEl,
    expanded,
    onToggle,
    children,
  }) => (
    <Box
      ref={refEl}
      sx={{
        scrollMarginTop: "110px",
      }}
    >
      <Accordion
        expanded={expanded}
        onChange={() => onToggle((previous) => !previous)}
        elevation={0}
        sx={{
          border: "1px solid #eaeaea",
          borderRadius: 3,
          overflow: "hidden",

          "&:before": {
            display: "none",
          },

          mb: 3,
        }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box>
            <Typography
              variant="h5"
              sx={{
                marginBottom: "5px",
              }}
            >
              {title}
            </Typography>

            {subtitle ? (
              <Typography
                variant="body2"
                sx={{
                  color: "#555",
                  mt: 0.5,
                }}
              >
                {subtitle}
              </Typography>
            ) : null}
          </Box>
        </AccordionSummary>

        <AccordionDetails
          sx={{
            pt: 0,
          }}
        >
          <Divider
            sx={{
              mb: 2,
            }}
          />

          {children}
        </AccordionDetails>
      </Accordion>
    </Box>
  );

  // =====================================================
  // PAGE
  // =====================================================

  return (
    <Box
      sx={{
        width: "auto",

        padding: {
          xs: "18px",
          sm: "26px",
          md: "24px 80px",
        },

        backgroundColor: "#fff",
        borderRadius: "8px",
      }}
    >
      {/* =================================================
          ARCHIVE HEADER
      ================================================= */}

      <Box
        sx={{
          textAlign: "center",
          mb: 3,
        }}
      >
        <Typography
          variant="overline"
          sx={{
            letterSpacing: 2,
            fontWeight: 700,
            color: "#555",
          }}
        >
          ARCHIVE
        </Typography>

        <Typography
          variant="h4"
          sx={{
            marginBottom: "30px",
            textAlign: "center",
            paddingBottom: "5px",
            borderBottom: "2px solid #FFD700",
          }}
        >
          Summer Residency 2026
        </Typography>

        <Typography
          variant="body1"
          sx={{
            maxWidth: 900,
            mx: "auto",
            lineHeight: 1.8,
            mt: 1,
            color: "#333",
          }}
        >
          A single archive page with Summer Residency 2026 content embedded
          below—including the program overview, participants and their digital
          humanities projects, guest speakers, workshop schedule, and workshop
          materials.
        </Typography>

        {/* Archive metadata */}

        <Stack
          direction="row"
          spacing={1}
          useFlexGap
          flexWrap="wrap"
          justifyContent="center"
          sx={{
            mt: 2,
          }}
        >
          {archiveMeta.map((item) => (
            <Chip
              key={item.label}
              label={item.label}
              size="small"
              sx={{
                backgroundColor: "#f6f6f6",
                border: "1px solid #e6e6e6",
                fontWeight: 700,
              }}
            />
          ))}
        </Stack>
      </Box>

      {/* =================================================
          ARCHIVE NOTICE + CONTROLS
      ================================================= */}

      <Box
        sx={{
          border: "1px solid #ffe58f",
          backgroundColor: "#fffbe6",

          borderRadius: 2,

          px: 2,
          py: 1.5,

          mb: 3,

          display: "flex",

          flexDirection: {
            xs: "column",
            sm: "row",
          },

          alignItems: {
            xs: "flex-start",
            sm: "center",
          },

          justifyContent: "space-between",

          gap: 2,
        }}
      >
        <Typography
          variant="body2"
          sx={{
            color: "#333",
            lineHeight: 1.7,
          }}
        >
          <b>Archive:</b> This page preserves content from the completed Summer
          Residency 2026 program.
        </Typography>

        <Stack
          direction="row"
          spacing={1}
          sx={{
            flexWrap: "wrap",
          }}
        >
          <Button
            variant="outlined"
            onClick={expandAll}
            sx={{
              borderColor: "#111",
              color: "#111",

              "&:hover": {
                borderColor: "#000",
                backgroundColor: "#f5f5f5",
              },
            }}
          >
            Expand all
          </Button>

          <Button
            variant="outlined"
            onClick={collapseAll}
            sx={{
              borderColor: "#111",
              color: "#111",

              "&:hover": {
                borderColor: "#000",
                backgroundColor: "#f5f5f5",
              },
            }}
          >
            Collapse all
          </Button>
        </Stack>
      </Box>

      {/* =================================================
          QUICK LINKS
      ================================================= */}

      <Typography
        variant="h5"
        sx={{
          mb: 1,
        }}
      >
        Quick Links
      </Typography>

      <Typography
        variant="body2"
        sx={{
          color: "#555",
          mb: 2,
        }}
      >
        Jump to a section below (no redirection).
      </Typography>

      <Grid
        container
        spacing={3}
        sx={{
          mb: 4,
        }}
      >
        {quickLinks.map((item) => (
          <Grid
            item
            xs={12}
            sm={6}
            md={4}
            lg
            key={item.id}
            sx={{
              minWidth: {
                lg: 0,
              },
            }}
          >
            <QuickCard item={item} />
          </Grid>
        ))}
      </Grid>

      <Divider
        sx={{
          my: 4,
        }}
      />

      {/* =================================================
          ABOUT
      ================================================= */}

      <SectionShell
        title="About"
        subtitle="Program overview and information about the 2026 Summer Residency."
        refEl={aboutRef}
        expanded={open.about}
        onToggle={(fn) =>
          setOpen((previous) => ({
            ...previous,
            about: fn(previous.about),
          }))
        }
      >
        <AboutSummerResidency2026 />
      </SectionShell>

      {/* =================================================
          PARTICIPANTS & PROJECTS
      ================================================= */}

      <SectionShell
        title="Participants & Projects"
        subtitle="Meet the 2026 participants and explore the digital humanities projects they developed during the residency."
        refEl={participantsRef}
        expanded={open.participants}
        onToggle={(fn) =>
          setOpen((previous) => ({
            ...previous,
            participants: fn(previous.participants),
          }))
        }
      >
        <Participants2026 />
      </SectionShell>

      {/* =================================================
          GUEST SPEAKERS
      ================================================= */}

      <SectionShell
        title="Guest Speakers"
        subtitle="Meet the visiting speakers, librarians, faculty, archivists, and cultural heritage professionals who contributed to the residency."
        refEl={speakersRef}
        expanded={open.speakers}
        onToggle={(fn) =>
          setOpen((previous) => ({
            ...previous,
            speakers: fn(previous.speakers),
          }))
        }
      >
        <GuestSpeakers />
      </SectionShell>

      {/* =================================================
          WORKSHOP SCHEDULE
      ================================================= */}

      <SectionShell
        title="Workshop Schedule"
        subtitle="Six weeks of workshops, guest speakers, research activities, field visits, and project development."
        refEl={scheduleRef}
        expanded={open.schedule}
        onToggle={(fn) =>
          setOpen((previous) => ({
            ...previous,
            schedule: fn(previous.schedule),
          }))
        }
      >
        <ScheduleSummer2026 />
      </SectionShell>

      {/* =================================================
          WORKSHOP MATERIALS
      ================================================= */}

      <SectionShell
        title="Workshop Materials"
        subtitle="Weekly readings, research resources, workshop links, and supporting materials."
        refEl={materialsRef}
        expanded={open.materials}
        onToggle={(fn) =>
          setOpen((previous) => ({
            ...previous,
            materials: fn(previous.materials),
          }))
        }
      >
        <WeeklyReadings />
      </SectionShell>

      {/* =================================================
          BOTTOM TIP
      ================================================= */}

      <Box
        sx={{
          mt: 4,
          textAlign: "center",
          opacity: 0.9,
        }}
      >
        <Typography
          variant="caption"
          sx={{
            display: "block",
            color: "#666",
          }}
        >
          Tip: Use the “Quick Links” to jump to sections, and expand/collapse
          sections to keep the page tidy.
        </Typography>
      </Box>
    </Box>
  );
};

export default SummerWorkshopArchive2026;
